import React from "react";

// Shared rendering for a result's "Unique Selling Point" badge.
//
// The badge is driven entirely by the hotel's uniqueSellingPoint object from the API:
//   { displayName: string, highlightColour: "#rrggbb", displayWithGlow: boolean }
// Colours are authored in Umbraco as arbitrary hex, so they can't be Tailwind classes — the
// card border/glow, the corner flag and the "View deal" button are tinted via inline styles.
//
// Mapping (agreed with the business):
//   - corner flag  : displayName, on a highlightColour background
//   - card         : 2px highlightColour border + glow, ONLY when displayWithGlow is on
//   - View deal btn : tinted to highlightColour (any USP)

/** Convert "#rgb" / "#rrggbb" to "rgba(r,g,b,a)". Falls back to the raw value if unparseable. */
function withAlpha(hex, alpha) {
    if (typeof hex !== "string") {
        return hex;
    }
    let h = hex.trim().replace(/^#/, "");
    if (h.length === 3) {
        h = h.split("").map((c) => c + c).join("");
    }
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) {
        return hex;
    }
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Inline style for the result card: coloured border + glow, only when the USP asks for a glow. */
export function uspCardStyle(usp) {
    if (!usp || !usp.highlightColour || !usp.displayWithGlow) {
        return undefined;
    }
    return {
        borderColor: usp.highlightColour,
        borderWidth: "2px",
        boxShadow: `0 0 1.5625rem ${withAlpha(usp.highlightColour, 0.6)}`,
    };
}

/** Inline style tinting the "View deal" button to the USP colour. */
export function uspButtonStyle(usp) {
    if (!usp || !usp.highlightColour) {
        return undefined;
    }
    return { backgroundColor: usp.highlightColour };
}

/** The corner flag badge shown over the result image. Renders nothing without a USP label
    (a colour-only / whitespace-label USP still gets its card glow + button tint, just no flag). */
export function UspFlag({ usp }) {
    const label = usp?.displayName?.trim();
    if (!label) {
        return null;
    }
    return (
        <div className="boost-flag" style={{ backgroundColor: usp.highlightColour }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                <path d="M10 0a10 10 0 100 20 10 10 0 000-20zm5.03 7.53l-6 6a.75.75 0 01-1.06 0l-3-3a.75.75 0 111.06-1.06l2.47 2.47 5.47-5.47a.75.75 0 011.06 1.06z" />
            </svg>
            {label}
        </div>
    );
}
